import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle, FontFamily, FontSize, Color } from '@tiptap/extension-text-style';
import Typography from '@tiptap/extension-typography';
import Underline from '@tiptap/extension-underline';
import { Node, mergeAttributes } from '@tiptap/core';
import DOMPurify from 'dompurify';
import PlaceholderPalette from './PlaceholderPalette';
import ConfirmDialog from '../common/ConfirmDialog';
import UploadingModal from '../common/UploadingModal';
import customDocumentService from '../../services/api/customDocumentService';
import { groupingsService } from '../../services/api/groupingsService';
import { clientService } from '../../services/clientService';
import type {
  DocumentOutputFormat,
  CustomPlaceholder,
  PlaceholderPaletteItem,
} from '../../types/customDocuments';

// ============================================================
// PlaceholderChip — custom TipTap inline node
// ============================================================

const PlaceholderChip = Node.create({
  name: 'placeholderChip',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      token: { default: null },
      label: { default: null },
      namespace: { default: null },
      placeholderType: { default: null },
      imageWidth: { default: null },
      imageHeight: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-placeholder]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const { imageWidth, imageHeight, placeholderType } = HTMLAttributes;
    const sizeSuffix = placeholderType === 'image' && (imageWidth || imageHeight)
      ? ` (${imageWidth || '?'}×${imageHeight || '?'}px)`
      : '';

    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-placeholder': HTMLAttributes.token,
        'data-type': 'placeholder-chip',
        'data-placeholder-type': placeholderType || '',
        'data-image-width': imageWidth || '',
        'data-image-height': imageHeight || '',
        class: `placeholder-chip placeholder-chip--${HTMLAttributes.namespace}`,
        contenteditable: 'false',
      }),
      HTMLAttributes.label + sizeSuffix,
    ];
  },
});

// ============================================================
// Helpers
// ============================================================

function serializeNode(node: any, listContext?: { type: 'bullet' | 'ordered'; index: number }): string {
  if (node.type === 'placeholderChip') {
    const { token, imageWidth, imageHeight } = node.attrs;
    if (imageWidth || imageHeight) {
      return `${token.slice(0, -2)}|${imageWidth || ''}x${imageHeight || ''}}}`;
    }
    return token;
  }
  if (node.text) {
    return node.text;
  }
  if (node.type === 'bulletList' && node.content) {
    return node.content.map((child: any, i: number) =>
      serializeNode(child, { type: 'bullet', index: i })
    ).join('');
  }
  if (node.type === 'orderedList' && node.content) {
    return node.content.map((child: any, i: number) =>
      serializeNode(child, { type: 'ordered', index: i })
    ).join('');
  }
  if (node.type === 'listItem' && node.content) {
    const inner = node.content.map((child: any) => serializeNode(child)).join('');
    // inner ends with \n from the paragraph serialization
    const prefix = listContext?.type === 'ordered'
      ? `[ORDERED:${(listContext.index ?? 0) + 1}]`
      : '[BULLET]';
    return prefix + inner;
  }
  if (node.content) {
    const inner = node.content.map((child: any) => serializeNode(child)).join('');
    if (node.type === 'paragraph') return inner + '\n';
    if (node.type === 'hardBreak') return '\n';
    return inner;
  }
  return '';
}

function extractPlainTokens(editor: any): string {
  const json = editor.getJSON();
  return serializeNode(json).trim();
}

// ============================================================
// Toolbar
// ============================================================

interface ToolbarProps {
  editor: any;
  onFontChange?: (font: string) => void;
  onFontSizeChange?: (size: number) => void;
}

const FONT_FAMILIES = [
  { label: 'Manrope', value: '' },
  { label: 'Arial', value: 'Arial' },
  { label: 'Courier New', value: 'Courier New' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Times New Roman', value: 'Times New Roman' },
  { label: 'Verdana', value: 'Verdana' },
];

const FONT_SIZES = [
  { label: '14', value: '' },
  { label: '10', value: '10px' },
  { label: '12', value: '12px' },
  { label: '16', value: '16px' },
  { label: '18', value: '18px' },
  { label: '20', value: '20px' },
  { label: '24', value: '24px' },
  { label: '28', value: '28px' },
  { label: '32', value: '32px' },
];

const EditorToolbar: React.FC<ToolbarProps> = ({ editor, onFontChange, onFontSizeChange }) => {
  if (!editor) return null;

  return (
    <div className="template-editor-toolbar">
      <div className="toolbar-group">
        <button
          type="button"
          className={`toolbar-btn ${editor.isActive('bold') ? 'active' : ''}`}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className={`toolbar-btn ${editor.isActive('italic') ? 'active' : ''}`}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          className={`toolbar-btn ${editor.isActive('underline') ? 'active' : ''}`}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline"
        >
          <u>U</u>
        </button>
      </div>

      <div className="toolbar-divider" />

      <div className="toolbar-group">
        <button
          type="button"
          className={`toolbar-btn ${editor.isActive('bulletList') ? 'active' : ''}`}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet List"
        >
          &#8226;
        </button>
        <button
          type="button"
          className={`toolbar-btn ${editor.isActive('orderedList') ? 'active' : ''}`}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Ordered List"
        >
          1.
        </button>
      </div>

      <div className="toolbar-divider" />

      <div className="toolbar-group">
        <button
          type="button"
          className={`toolbar-btn ${editor.isActive({ textAlign: 'left' }) ? 'active' : ''}`}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          title="Align Left"
        >
          &#8676;
        </button>
        <button
          type="button"
          className={`toolbar-btn ${editor.isActive({ textAlign: 'center' }) ? 'active' : ''}`}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          title="Align Center"
        >
          &#8596;
        </button>
        <button
          type="button"
          className={`toolbar-btn ${editor.isActive({ textAlign: 'right' }) ? 'active' : ''}`}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          title="Align Right"
        >
          &#8677;
        </button>
      </div>

      <div className="toolbar-divider" />

      <div className="toolbar-group">
        <select
          className="toolbar-select"
          value={editor.getAttributes('textStyle').fontFamily || ''}
          onChange={(e) => {
            const fontLabel = FONT_FAMILIES.find(f => f.value === e.target.value)?.label || 'Manrope';
            onFontChange?.(fontLabel);
            if (e.target.value) {
              editor.chain().focus().setFontFamily(e.target.value).run();
            } else {
              editor.chain().focus().unsetFontFamily().run();
            }
          }}
        >
          {FONT_FAMILIES.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
        <select
          className="toolbar-select toolbar-select-size"
          value={editor.getAttributes('textStyle').fontSize || ''}
          onChange={(e) => {
            const sizeLabel = FONT_SIZES.find(s => s.value === e.target.value)?.label || '14';
            onFontSizeChange?.(parseInt(sizeLabel, 10));
            if (e.target.value) {
              editor.chain().focus().setFontSize(e.target.value).run();
            } else {
              editor.chain().focus().unsetFontSize().run();
            }
          }}
        >
          {FONT_SIZES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      <div className="toolbar-divider" />

      <div className="toolbar-group">
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Horizontal Rule"
        >
          &#8212;
        </button>
      </div>
    </div>
  );
};

// ============================================================
// ImageSizePopover — appears when clicking an image-type chip
// ============================================================

interface ImageSizePopoverProps {
  pos: number;
  currentWidth: string;
  currentHeight: string;
  anchorRect: { top: number; left: number };
  onApply: (pos: number, width: string, height: string) => void;
  onClose: () => void;
}

const ImageSizePopover: React.FC<ImageSizePopoverProps> = ({
  pos,
  currentWidth,
  currentHeight,
  anchorRect,
  onApply,
  onClose,
}) => {
  const { t } = useTranslation();
  const [width, setWidth] = useState(currentWidth);
  const [height, setHeight] = useState(currentHeight);

  return (
    <>
      <div className="chip-popover-backdrop" onClick={onClose} />
      <div
        className="chip-popover"
        style={{ top: anchorRect.top + 28, left: anchorRect.left }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="chip-popover-title">{t('admin.customDocuments.editor.imagePopover.title', 'Image size (px)')}</div>
        <div className="chip-popover-fields">
          <input
            type="number"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            placeholder={t('admin.customDocuments.editor.imagePopover.width', 'Width')}
            min={1}
            max={2000}
            className="chip-popover-input"
          />
          <span className="dimension-separator">&times;</span>
          <input
            type="number"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            placeholder={t('admin.customDocuments.editor.imagePopover.height', 'Height')}
            min={1}
            max={2000}
            className="chip-popover-input"
          />
        </div>
        <div className="chip-popover-hint">{t('admin.customDocuments.editor.imagePopover.hint', 'Leave blank for original size')}</div>
        <div className="chip-popover-actions">
          <button type="button" className="btn-secondary btn-sm" onClick={onClose}>{t('admin.customDocuments.editor.imagePopover.cancel', 'Cancel')}</button>
          <button
            type="button"
            className="btn-primary btn-sm"
            onClick={() => onApply(pos, width, height)}
          >
            {t('admin.customDocuments.editor.imagePopover.apply', 'Apply')}
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================
// TemplateEditor
// ============================================================

interface TemplateEditorProps {
  clientId: string;
  templateId?: string;
  onSave: () => void;
  onCancel: () => void;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({ clientId, templateId: templateIdProp, onSave, onCancel }) => {
  const { t } = useTranslation();
  // Track effective templateId — may be set after preview auto-creates a new template
  const [effectiveTemplateId, setEffectiveTemplateId] = useState<string | undefined>(templateIdProp);
  const templateId = effectiveTemplateId;
  const isEditMode = !!templateId;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [context, setContext] = useState<string>('');
  const [showPreviewMenu, setShowPreviewMenu] = useState(false);
  const [defaultFont, setDefaultFont] = useState('Manrope');
  const [defaultFontSize, setDefaultFontSize] = useState(14);
  const [customPlaceholders, setCustomPlaceholders] = useState<CustomPlaceholder[]>([]);
  const [groupingLabels, setGroupingLabels] = useState<{ grouping1?: string; grouping2?: string; grouping3?: string }>({});
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [charCount, setCharCount] = useState(0);
  const [, setSelectionKey] = useState(0);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [imagePopover, setImagePopover] = useState<{
    pos: number;
    width: string;
    height: string;
    anchorRect: { top: number; left: number };
  } | null>(null);
  const skipReloadRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      FontFamily,
      FontSize,
      Color,
      Typography,
      Underline,
      PlaceholderChip,
    ],
    onUpdate: ({ editor }) => {
      setIsDirty(true);
      const plain = extractPlainTokens(editor);
      setCharCount(plain.length);
    },
    onSelectionUpdate: () => {
      setSelectionKey((k) => k + 1);
    },
  });

  // Load template data (edit mode) and custom placeholders
  useEffect(() => {
    // Skip reload when templateId changed from an auto-save during preview
    if (skipReloadRef.current) {
      skipReloadRef.current = false;
      return;
    }
    const loadData = async () => {
      setLoading(true);
      try {
        // Load custom placeholders for the palette
        const config = await customDocumentService.getConfig(clientId);
        setCustomPlaceholders(config.customPlaceholders || []);

        // Load groupings config for dynamic Grouping 1/2 labels
        const gConfig = await groupingsService.getGroupings(clientId);
        if (gConfig) {
          setGroupingLabels({
            grouping1: gConfig.grouping1Label || undefined,
            grouping2: gConfig.grouping2Label || undefined,
            grouping3: gConfig.grouping3Label || undefined,
          });
        }

        // Load template if editing
        if (templateId) {
          const template = await customDocumentService.getTemplate(clientId, templateId);
          setName(template.name);
          setDescription(template.description || '');
          setContext(template.context || '');
          setDefaultFont(template.defaultFont || 'Manrope');
          setDefaultFontSize(template.defaultFontSize || 14);
          if (editor && template.bodyHtml) {
            const cleanHtml = DOMPurify.sanitize(template.bodyHtml, {
              ALLOWED_TAGS: ['p', 'br', 'hr', 'strong', 'em', 'u', 's', 'ul', 'ol', 'li', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
              ALLOWED_ATTR: ['style', 'class', 'data-placeholder', 'data-type', 'data-placeholder-type', 'data-image-width', 'data-image-height', 'contenteditable', 'token', 'label', 'namespace', 'placeholdertype', 'imagewidth', 'imageheight'],
            });
            editor.commands.setContent(cleanHtml);
            setCharCount(extractPlainTokens(editor).length);
          }
        }
      } catch (err: any) {
        setError(err.message || t('admin.customDocuments.editor.errors.loadFailed', 'Failed to load data'));
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [clientId, templateId, editor]);

  const insertPlaceholder = useCallback((item: PlaceholderPaletteItem) => {
    if (!editor) return;
    editor.chain().focus().insertContent({
      type: 'placeholderChip',
      attrs: {
        token: item.token,
        label: item.label,
        namespace: item.namespace,
        placeholderType: item.type || null,
      },
    }).run();
  }, [editor]);

  // Handle click on an image-type placeholder chip to show size popover
  const handleEditorClick = useCallback((e: React.MouseEvent) => {
    if (!editor) return;
    const target = e.target as HTMLElement;
    const chip = target.closest('[data-placeholder-type="image"]') as HTMLElement;
    if (!chip) {
      setImagePopover(null);
      return;
    }

    // Find the node position in the document
    const view = editor.view;
    const pos = view.posAtDOM(chip, 0);
    if (pos == null) return;

    const node = view.state.doc.nodeAt(pos);
    if (!node || node.type.name !== 'placeholderChip') return;

    const rect = chip.getBoundingClientRect();
    const container = (chip.closest('.template-canvas-container') || chip.parentElement)!;
    const canvasRect = container.getBoundingClientRect();

    // If chip is in the bottom 160px of the container, show popover above
    const spaceBelow = canvasRect.bottom - rect.bottom;
    const popoverHeight = 160; // approximate popover height
    const showAbove = spaceBelow < popoverHeight;

    const popoverWidth = 220;
    const rawLeft = rect.left - canvasRect.left;
    const maxLeft = canvasRect.width - popoverWidth;
    const clampedLeft = Math.max(0, Math.min(rawLeft, maxLeft));

    setImagePopover({
      pos,
      width: node.attrs.imageWidth?.toString() || '',
      height: node.attrs.imageHeight?.toString() || '',
      anchorRect: {
        top: showAbove
          ? rect.top - canvasRect.top - popoverHeight
          : rect.bottom - canvasRect.top + 4,
        left: clampedLeft,
      },
    });
  }, [editor]);

  const applyImageSize = useCallback((pos: number, width: string, height: string) => {
    if (!editor) return;
    const { tr } = editor.view.state;
    const node = tr.doc.nodeAt(pos);
    if (!node) return;

    const attrs = {
      ...node.attrs,
      imageWidth: width ? parseInt(width, 10) : null,
      imageHeight: height ? parseInt(height, 10) : null,
    };
    editor.view.dispatch(tr.setNodeMarkup(pos, undefined, attrs));
    setIsDirty(true);
    setImagePopover(null);
  }, [editor]);

  const handleSave = async () => {
    if (!editor) return;
    if (!name.trim()) {
      setError(t('admin.customDocuments.editor.errors.nameRequired', 'Template Name is required'));
      return;
    }
    if (name.length > 200) {
      setError(t('admin.customDocuments.editor.errors.nameTooLong', 'Template Name must be 200 characters or less'));
      return;
    }

    const bodyHtml = editor.getHTML();
    const bodyPlainTokens = extractPlainTokens(editor);

    if (!bodyPlainTokens) {
      setError(t('admin.customDocuments.editor.errors.bodyEmpty', 'Template body cannot be empty'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isEditMode && templateId) {
        await customDocumentService.updateTemplate(clientId, templateId, {
          name: name.trim(),
          description: description.trim() || undefined,
          context: context || undefined,
          bodyHtml,
          bodyPlainTokens,
          defaultFont,
          defaultFontSize,
        });
      } else {
        await customDocumentService.createTemplate(clientId, {
          name: name.trim(),
          description: description.trim() || undefined,
          context: context || undefined,
          bodyHtml,
          bodyPlainTokens,
          defaultFont,
          defaultFontSize,
          isActive: true,
        });
      }
      setIsDirty(false);
      onSave();
    } catch (err: any) {
      setError(err.message || t('admin.customDocuments.editor.saveError', 'Failed to save template'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowCancelConfirm(true);
      return;
    }
    onCancel();
  };

  const handlePreview = async (format: DocumentOutputFormat) => {
    if (!editor || !name.trim()) {
      setError(t('admin.customDocuments.editor.errors.nameRequiredForPreview', 'Template name is required to preview'));
      return;
    }

    setPreviewing(true);
    setError(null);

    try {
      // 1. Auto-save the template first
      const bodyHtml = editor.getHTML();
      const bodyPlainTokens = extractPlainTokens(editor);

      if (!bodyPlainTokens) {
        setError(t('admin.customDocuments.editor.errors.bodyEmpty', 'Template body cannot be empty'));
        setPreviewing(false);
        return;
      }

      let savedTemplateId = templateId;

      if (isEditMode && templateId) {
        await customDocumentService.updateTemplate(clientId, templateId, {
          name: name.trim(),
          description: description.trim() || undefined,
          context: context || undefined,
          bodyHtml,
          bodyPlainTokens,
          defaultFont,
          defaultFontSize,
        });
      } else {
        const created = await customDocumentService.createTemplate(clientId, {
          name: name.trim(),
          description: description.trim() || undefined,
          context: context || undefined,
          bodyHtml,
          bodyPlainTokens,
          defaultFont,
          defaultFontSize,
          isActive: true,
        });
        savedTemplateId = created.templateId;
        // Switch to edit mode so subsequent saves update instead of creating again
        skipReloadRef.current = true;
        setEffectiveTemplateId(savedTemplateId);
      }
      setIsDirty(false);

      if (!savedTemplateId) {
        setError(t('admin.customDocuments.editor.errors.templateIdFailed', 'Failed to determine template ID after save'));
        setPreviewing(false);
        return;
      }

      // 2. Fetch the most recent trade
      const trades = await clientService.getUnmatchedTrades(clientId, true);
      if (!trades || trades.length === 0) {
        setError(t('admin.customDocuments.editor.errors.noTradesForPreview', 'No trades found for this client. Book a trade first to preview.'));
        setPreviewing(false);
        return;
      }
      const tradeId = trades[0].id;

      // 3. Generate a document
      const results = await customDocumentService.generateDocuments(clientId, {
        tradeIds: [tradeId],
        templateId: savedTemplateId,
        outputFormat: format,
        trigger: 'PREVIEW',
      });

      if (!results || results.length === 0 || results[0].status === 'FAILED') {
        setError(results?.[0]?.errorMessage || t('admin.customDocuments.editor.errors.generationFailed', 'Document generation failed'));
        setPreviewing(false);
        return;
      }

      // 4. Get download URL and open in new tab
      const { signedUrl } = await customDocumentService.getDownloadUrl(
        clientId,
        results[0].recordId
      );
      window.open(signedUrl, '_blank');

    } catch (err: any) {
      setError(err.message || t('admin.customDocuments.editor.errors.previewFailed', 'Preview failed'));
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="template-editor-overlay">
        <div className="tab-loading">
          <div className="spinner" />
          <p>{t('admin.customDocuments.editor.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="template-editor-overlay">
      <div className="template-editor-header">
        <h3>
          {isEditMode
            ? t('admin.customDocuments.editor.title.edit', 'Edit Template')
            : t('admin.customDocuments.editor.title.create', 'New Template')}
        </h3>
      </div>

      <div className="template-editor-fields">
        <div className="form-group">
          <label>{t('admin.customDocuments.editor.fields.name', 'Template Name')}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setIsDirty(true); }}
            maxLength={200}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label>{t('admin.customDocuments.editor.fields.description', 'Description')}</label>
          <input
            type="text"
            value={description}
            onChange={(e) => { setDescription(e.target.value); setIsDirty(true); }}
            maxLength={500}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label>{t('admin.customDocuments.editor.fields.context', 'Context')}</label>
          <select
            value={context}
            onChange={(e) => { setContext(e.target.value); setIsDirty(true); }}
            className="form-input"
          >
            <option value="">{t('admin.customDocuments.editor.fields.contextGeneral', 'General')}</option>
            <option value="settlement_instructions">{t('admin.customDocuments.editor.fields.contextSettlement', 'Settlement Instructions')}</option>
          </select>
        </div>
      </div>

      <EditorToolbar
        editor={editor}
        onFontChange={(f) => { setDefaultFont(f); setIsDirty(true); }}
        onFontSizeChange={(s) => { setDefaultFontSize(s); setIsDirty(true); }}
      />

      <div className="template-editor-layout">
        <div className="template-canvas-container" onClick={handleEditorClick}>
          <EditorContent editor={editor} className="template-canvas" />
          {imagePopover && (
            <ImageSizePopover
              pos={imagePopover.pos}
              currentWidth={imagePopover.width}
              currentHeight={imagePopover.height}
              anchorRect={imagePopover.anchorRect}
              onApply={applyImageSize}
              onClose={() => setImagePopover(null)}
            />
          )}
        </div>
        <PlaceholderPalette
          customPlaceholders={customPlaceholders}
          onInsert={insertPlaceholder}
          groupingLabels={groupingLabels}
        />
      </div>

      <div className="template-editor-footer">
        <div className="editor-status">
          <span className={charCount > 50000 ? 'char-count-warning' : 'char-count'}>
            {t('admin.customDocuments.editor.characterCount', '{{count}} characters', { count: charCount })}
          </span>
          {charCount > 50000 && (
            <span className="char-warning-text">
              {t('admin.customDocuments.editor.characterWarning', 'Template body exceeds 50,000 characters')}
            </span>
          )}
        </div>

        {error && <div className="editor-error">{error}</div>}

        <div className="editor-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleCancel}
            disabled={saving || previewing}
          >
            {t('admin.customDocuments.editor.cancel', 'Cancel')}
          </button>
          <div className="preview-split-btn">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowPreviewMenu(!showPreviewMenu)}
              disabled={saving || previewing}
            >
              {previewing ? t('admin.customDocuments.editor.generating', 'Generating...') : t('admin.customDocuments.editor.preview', 'Preview')} &#9662;
            </button>
            {showPreviewMenu && (
              <div className="preview-split-menu">
                <button type="button" onClick={() => { setShowPreviewMenu(false); handlePreview('DOCX'); }}>
                  DOCX
                </button>
                <button type="button" onClick={() => { setShowPreviewMenu(false); handlePreview('PDF'); }}>
                  PDF
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || previewing}
          >
            {saving ? t('admin.customDocuments.editor.saving', 'Saving...') : t('admin.customDocuments.editor.save', 'Save')}
          </button>
        </div>
      </div>
      <UploadingModal isOpen={previewing} message={t('admin.customDocuments.editor.generatingPreview', 'Generating preview...')} />
      <ConfirmDialog
        isOpen={showCancelConfirm}
        title={t('admin.customDocuments.confirm.unsavedChanges', 'Unsaved Changes')}
        message={t('admin.customDocuments.editor.unsavedChanges', 'You have unsaved changes. Leave anyway?')}
        confirmLabel={t('admin.customDocuments.confirm.leave', 'Leave')}
        cancelLabel={t('admin.customDocuments.confirm.stay', 'Stay')}
        onConfirm={() => { setShowCancelConfirm(false); onCancel(); }}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </div>
  );
};

export default TemplateEditor;
